import { useEffect } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { useStrippingContext } from './useStrippingContext';

const schema = z.object({
  stripperTipLotNumber: z.string().min(1, 'QC Lot Number is required'),
});

type FormValues = z.infer<typeof schema>;

interface IQCDialogProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
}

export const QCDialog: React.FC<IQCDialogProps> = ({ visible, setVisible }) => {
  const { setQcStripperTipLotNumber, qcStripperTipLotNumber } =
    useStrippingContext();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      stripperTipLotNumber: qcStripperTipLotNumber,
    },
  });

  useEffect(() => {
    if (visible) {
      reset({ stripperTipLotNumber: qcStripperTipLotNumber });
    }
  }, [visible, qcStripperTipLotNumber, reset]);

  const onSubmit = async (data: FormValues) => {
    try {
      setQcStripperTipLotNumber(data.stripperTipLotNumber);
      setVisible(false);
    } catch (error) {
      console.error('Failed to set QC lot number:', error);
    }
  };

  const footerContent = (
    <>
      <Button
        label="Cancel"
        icon="pi pi-times"
        severity="secondary"
        onClick={() => {
          reset();
          setVisible(false);
        }}
      />
      <Button
        label="Save"
        icon="pi pi-check"
        onClick={handleSubmit(onSubmit)}
      />
    </>
  );

  return (
    <Dialog
      header="QC Stripper Tip Lot Number"
      visible={visible}
      onHide={() => setVisible(false)}
      style={{ width: '30rem' }}
      footer={footerContent}
      modal
      className="p-fluid"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="qc-form">
        <div className="label-input-group">
          <label htmlFor="stripperTipLotNumber">QC Lot Number</label>
          <Controller
            name="stripperTipLotNumber"
            control={control}
            render={({ field, fieldState }) => (
              <InputText
                id={field.name}
                value={field.value}
                className={fieldState.invalid ? 'p-invalid' : ''}
                onChange={(e) => field.onChange(e.target.value)}
                autoFocus
              />
            )}
          />
          {errors.stripperTipLotNumber && (
            <small className="p-error">
              {errors.stripperTipLotNumber.message}
            </small>
          )}
        </div>
      </form>
    </Dialog>
  );
};
